import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { Checkbox, Grid, Select, useMantineColorScheme } from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import _, { set } from "lodash";
import PropTypes from "prop-types";
import { addedStepsAtom } from "../../../utils/atoms/addedStepsAtom";
import { componentsAtom } from "../../../utils/atoms/componentsAtom";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../../utils/atoms/projectDetailsAtom";
import { componentStatusAtom } from "../../../utils/atoms/componentStatus";
import MGAAPI from "../../../api/index.js";
import displayError from "../../../constants/utils.js";
import { mgaRegex, responsiveCol, useAssessmentRouteParams, useCurrentDesignComponentLists } from "../../../constants/helper.js";
import { AccordionItem } from "../../AccordianItem";
import CapexTable from "./capexTable.js";
import ControllerJson from "../../../components/help/helpCardMenu.json";
import { helpAtom } from "../../../utils/atoms/helpAtom.js";
import { useHelpFocusBlur } from "../../../utils/useHelpFocusBlur.js";
import { helpContextMenuAtom } from "../../../utils/atoms/helpContextMenuAtom.js";


const MGA_API = new MGAAPI();

const LoadController = forwardRef((props, ref) => {
  const { setLoadingComp } = props;
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const [componentDetails, setComponentDetails] = useAtom(componentsAtom);
  const [, setComponentStatus] = useAtom(componentStatusAtom);
  const [projectDetails,setProjectDetails] = useAtom(projectDetailsAtom);
  const [capexTableHasErrors, setCapexTableHasErrors] = useState(false);
  const [showCapexTableErrors, setShowCapexTableErrors] = useState(false);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const { designId, designVersionId, projectId } = useAssessmentRouteParams();
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(
    projectDetails?.designComponentsList
  );
  const hasBattery = _.some(selectedDesignAndDesignVersion?.countComponentDTOList, {
    componentType: "Battery",
  });
  const hasGenset = _.some(selectedDesignAndDesignVersion?.countComponentDTOList, {
    componentType: "Genset",
  });

  const form = useForm({
    key: JSON.stringify(componentDetails?.controller),
    initialValues: componentDetails?.controller || {},
    validate: {
      economicAttributes: {
        controllerParameters: {
          capex: (value) =>
            value === 0 || !value
              ? null
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(String(value))
              ? t("controller.validation.capexInvalid")
              : null,
          replacement: (value) =>
            value === 0 || !value
              ? null
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(String(value))
              ? t("controller.validation.replacementInvalid")
              : null,
          om: (value) =>
            value === 0 || !value
              ? null
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(String(value))
              ? t("controller.validation.omInvalid")
              : null,
          lifetimeYear: (value) =>
            value === 0 || !value
              ? null
              : !mgaRegex?.oneTo25Regex.test(value)
              ? t("controller.validation.lifetimeRange")
              : null,
        },
      },
    },
  });
  const { setValues } = form;

  useEffect(() => {
    if (!form.isDirty()) return;
    setComponentStatus((prev) => ({
      ...prev,
      controller: { isEditing: true, completed: false },
    }));
  }, [form.values]);

  const handleControllerSubmit = form.onSubmit(
    (values) => {
      handleSaveController();
    },
    (errors) => {
      const firstErrorPath = Object.keys(errors)[0];
      const inputNode = form.getInputNode(firstErrorPath);
      if (inputNode) {
        inputNode.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  );

  useImperativeHandle(ref, () => ({
    handleControllerSubmit,
  }));

  const containerRef = useRef(null);
  const accordianRef = useRef(null);

  useHelpFocusBlur(accordianRef, setHelperAtom, helperAtom);
  const handleContainerClick = (event, cardId) => {
    const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "LABEL"];
    if (interactiveTags.includes(event.target.tagName)) return;
    const cardElement = document.querySelector(`[card-id=${cardId}]`);
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
      cardElement.style.transition = "background-color 0.5s ease";
      cardElement.style.border = "2px solid #42b4e6";
      setTimeout(() => {
        cardElement.style.border = "";
      }, 2000);
    }
  };

  const handleContainerKeyDown = (event, cardId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContainerClick(event, cardId);
    }
  };

  const handleBlur = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setHelperAtom({
        ...helperAtom,
        isFocused: false,
        helpSection: {
          title: "",
          description: "",
          type: "",
          other: "",
        },
      });
    }
  };
  const handleFocus = (event) => {
    const dataId = event.target.getAttribute("data-id");
    if (dataId) {
      const cardElement = document.querySelector(`[card-id="${dataId}"]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
        cardElement.style.transition = "background-color 0.5s ease";
        cardElement.style.border = "2px solid #42b4e6";
        setTimeout(() => {
          cardElement.style.border = "";
        }, 2000);
      }
    }
  };

  const [helpMenuData] = useAtom(helpContextMenuAtom);

  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.controller?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
      // placeholder: field?.placeholder || "",
    };
  };

  useEffect(() => {
    const inputElements = containerRef.current.querySelectorAll(
      "input, select, textarea, file"
    );

    inputElements.forEach((inputElement) => {
      inputElement.addEventListener("focus", handleFocus);
      inputElement.addEventListener("blur", handleBlur);
    });

    return () => {
      inputElements.forEach((inputElement) => {
        inputElement.removeEventListener("focus", handleFocus);
        inputElement.removeEventListener("blur", handleBlur);
      });
    };
  }, []);

  const handleSaveController = () => {
    setShowCapexTableErrors(true);
    if (capexTableHasErrors) {
      displayError(
        "error",
        t("controller.capexTable.error", {
          defaultValue: "Please fix errors in Detailed Capex table",
        })
      );
      return;
    }
    setLoadingComp(true);
    const filteredComponent = selectedDesignAndDesignVersion?.componentDTOList.find(
      (comp) => comp.componentType === "Controller"
    );
    const payload = {
      projectId,
      designId:
        designId,
      designVersionId:
        designVersionId,
      componentType: "Controller",
      componentId: filteredComponent ? filteredComponent.componentId : -1,
      componentAttributes: {
        controllerParameters: {
          mainAnchor:
            componentDetails?.controller?.componentAttributes
              ?.controllerParameters?.mainAnchor,
          exportPowerBattery:
            componentDetails?.controller?.componentAttributes
              ?.controllerParameters?.exportPowerBattery,
          exportPowerGenset:
            componentDetails?.controller?.componentAttributes
              ?.controllerParameters?.exportPowerGenset,
        },
      },
      economicAttributes: {
        controllerParameters: {
          detailedExpenditure:
            componentDetails?.controller?.economicAttributes
              ?.controllerParameters?.detailedExpenditure || [],
        },
      },
      datasource: "OTHER",
    };
    MGA_API.saveLoadComponent(payload)
      .then((res) => {
        setComponentStatus((prev) => ({
          ...prev,
          controller: { isEditing: false, completed: true },
        }));
        MGA_API.editProject(
          projectId
        ).then((res) => {
          setProjectDetails((prev) => ({
            ...prev,
            userId: res.userId || "",
            currencyCode: res?.projectDTO?.currencyCode || "$",
            projectDTO: res.projectDTO || {},
            componentDTOList:
              flattenCountComponentDTOList(res.countComponentDTOList) || [],
            designWithVersionsDTOList: res.designWithVersionsDTOList || [],
            locationDTO: res.locationDTO || {},
            countComponentDTOList: res?.designComponentsList,
            // Use new utility function to deeply adapt designComponentsList
            designComponentsList: flattenDesignCountComponent(res.designComponentsList) || [],
          }));
        });
        setLoadingComp(false);
        displayError(
          "success",
          t("controller.save.success", {
            defaultValue: "Controller Details Added Successfully",
          })
        );
      })
      .catch((error) => {
        setLoadingComp(false);
        console.error("Error fetching data:", error);
      });
  };

  const handleControllerValues = (category, key, value) => {
    setComponentDetails((prev) => {
      const newState = { ...prev, currentComponent: "controller" };
      set(
        newState,
        `controller.${category}.controllerParameters.${key}`,
        value
      );
      setValues(newState?.controller);
      return newState;
    });
    setComponentStatus((prev) => ({
      ...prev,
      controller: { isEditing: true, completed: false },
    }));
  };

  const header1 = () => (
    <>
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("controller") + 1}`}: A.
      </span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        &nbsp;{t("controller.header.gridForming")}
      </span>
    </>
  );

  const children1 = () => (
    <div
      ref={accordianRef}
      onClick={(e) => handleContainerClick(e, "accordion-controller-3")}
      onKeyDown={(e) => handleContainerKeyDown(e, "accordion-controller-3")}
      data-id="accordion-controller-3"
      role="button"
      tabIndex={0}
    >
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-5")}
            data-id="field-5"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("controller.select.mainAnchor.label")}
            placeholder={t("controller.select.mainAnchor.placeholder")}
            value={
              componentDetails?.controller?.componentAttributes
                ?.controllerParameters?.mainAnchor
            }
            data={[
              { value: "Battery", label: t("common.battery"), disabled: !hasBattery },
              { value: "Genset", label: t("common.genset"), disabled: !hasGenset },
            ]}
            {...form.getInputProps(
              "componentAttributes.controllerParameters.mainAnchor"
            )}
            onChange={(value) =>
              handleControllerValues("componentAttributes", "mainAnchor", value)
            }
            withinPortal
            clearable
          />
        </Grid.Col>
      </Grid>
    </div>
  );
  const header2 = () => (
    <>
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("controller") + 1}`}: B.
      </span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        &nbsp;{t("controller.header.exportConstraints")}
      </span>
    </>
  );

  const children2 = () => (
    <div
      ref={accordianRef}
      onClick={(e) => handleContainerClick(e, "accordion-controller-4")}
      onKeyDown={(e) => handleContainerKeyDown(e, "accordion-controller-4")}
      data-id="accordion-controller-4"
      role="button"
      tabIndex={0}
    >
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Checkbox
            {...getFieldAttributes("field-6")}
            data-id="field-6"
            label={t("controller.checkbox.batteryPower")}
            color="seGreen.0"
            checked={
              componentDetails?.controller?.componentAttributes
                ?.controllerParameters?.exportPowerBattery
            }
            {...form.getInputProps(
              "componentAttributes.controllerParameters.exportPowerBattery"
            )}
            onChange={(event) => {
              const value = event.target.checked;
              handleControllerValues(
                "componentAttributes",
                "exportPowerBattery",
                value
              );
            }}
            disabled={!hasBattery}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <Checkbox
            {...getFieldAttributes("field-7")}
            data-id="field-7"
            label={t("controller.checkbox.gensetPower")}
            color="seGreen.0"
            checked={
              componentDetails?.controller?.componentAttributes
                ?.controllerParameters?.exportPowerGenset
            }
            {...form.getInputProps(
              "componentAttributes.controllerParameters.exportPowerGenset"
            )}
            onChange={(event) => {
              const value = event.target.checked;
              handleControllerValues(
                "componentAttributes",
                "exportPowerGenset",
                value
              );
            }}
            disabled={!hasGenset}
          />
        </Grid.Col>
      </Grid>
    </div>
  );

  const headerId2 = () => (
    <>
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("controller") + 1}`}: C.
      </span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        &nbsp;{t("controller.header.parameters")}
      </span>
    </>
  );

  const childrenId2 = () => (
    <div
      ref={accordianRef}
      onClick={(e) => handleContainerClick(e, "accordion-controller-2")}
      onKeyDown={(e) => handleContainerKeyDown(e, "accordion-controller-2")}
      data-id="accordion-controller-2"
      role="button"
      tabIndex={0}
    >
      <CapexTable
        capexItems={
          componentDetails?.controller?.economicAttributes?.controllerParameters
            ?.detailedExpenditure
        }
        onValidationChange={setCapexTableHasErrors}
        showErrors={showCapexTableErrors}
        onSave={(rows) => 
          handleControllerValues("economicAttributes", "detailedExpenditure", rows)
        }
        getFieldAttributes={getFieldAttributes}
      />
    </div>
  );
  return (
    <div ref={containerRef}>
      <AccordionItem id={1} header={header1()} key={1} children={children1()} />
      <AccordionItem id={2} header={header2()} key={2} children={children2()} />
      <AccordionItem id={3} header={headerId2()} key={3} children={childrenId2()} />
    </div>
  );
});
export default LoadController;

LoadController.propTypes = {
  setLoadingComp: PropTypes.func,
};
